/**
 * Типы и константы для feature-флагов (см. ADMIN_FEATURE_FLAGS_FRONTEND.md).
 *
 * Управляемые из админки переключатели функций, которые могут временно «лечь»:
 *   - rewrite             — рерайт документов;
 *   - antiplagiat_ru      — проверка через Антиплагиат.ВУЗ;
 *   - antiplagiat_domate  — проверка через Domate.
 *
 * Состояние приходит:
 *   1) при бутстрапе — `GET /feature-flags` (публично);
 *   2) в runtime — событие `feature-flags` по WebSocket
 *      (source: 'connect' | 'setFlag' | 'setAll').
 */

export type FeatureFlagKey = 'rewrite' | 'antiplagiat_ru' | 'antiplagiat_domate';

export interface FeatureFlagState {
  /** false → функция отключена, пользователь видит «временно недоступно». */
  enabled: boolean;
  /** Текст для пользователя; null → используем дефолтный из FEATURE_FLAG_DEFAULT_REASONS. */
  reason: string | null;
  /** ISO-метка последнего изменения. */
  updatedAt: string;
  /** userId админа, который менял; null — дефолт. */
  updatedBy: string | null;
}

export type FeatureFlagsState = Record<FeatureFlagKey, FeatureFlagState>;

export const FEATURE_FLAG_KEYS: FeatureFlagKey[] = [
  'rewrite',
  'antiplagiat_ru',
  'antiplagiat_domate',
];

export const FEATURE_FLAG_LABELS: Record<FeatureFlagKey, string> = {
  rewrite: 'Рерайт документов',
  antiplagiat_ru: 'Антиплагиат АП-ВУЗ',
  antiplagiat_domate: 'Антиплагиат Domate',
};

/** Дефолтные сообщения для пользователя, когда reason === null. */
export const FEATURE_FLAG_DEFAULT_REASONS: Record<FeatureFlagKey, string> = {
  rewrite: 'Перефразирование временно недоступно. Попробуйте позже.',
  antiplagiat_ru: 'Проверка через Антиплагиат.ВУЗ временно недоступна.',
  antiplagiat_domate: 'Проверка через Domate временно недоступна.',
};

/** Лимит на длину поля reason (определяется на бэке, дублируем для валидации формы). */
export const FEATURE_FLAG_REASON_MAX_LENGTH = 500;

export interface FeatureFlagsApiResponse {
  success: true;
  data: {
    state: FeatureFlagsState;
    keys: FeatureFlagKey[];
    labels: Record<FeatureFlagKey, string>;
  };
  message?: string;
}

export interface FeatureFlagsSocketEvent {
  state: FeatureFlagsState;
  /** Какой ключ изменился; null — bulk/connect. */
  key: FeatureFlagKey | null;
  source: 'connect' | 'setFlag' | 'setAll';
  updatedBy?: string | null;
}

export interface FeatureFlagUpdateResponse {
  success: true;
  message: string;
  data: {
    state: FeatureFlagsState;
    key: FeatureFlagKey;
    keys: FeatureFlagKey[];
    labels: Record<FeatureFlagKey, string>;
  };
}

export type DisabledFeatureCode =
  | 'rewrite_disabled'
  | 'antiplagiat_ru_disabled'
  | 'antiplagiat_domate_disabled';

export const FEATURE_FLAG_DISABLED_CODES: Record<FeatureFlagKey, DisabledFeatureCode> = {
  rewrite: 'rewrite_disabled',
  antiplagiat_ru: 'antiplagiat_ru_disabled',
  antiplagiat_domate: 'antiplagiat_domate_disabled',
};
